import {
    APP_INITIALIZER,
    ApplicationConfig,
    ErrorHandler,
    LOCALE_ID,
    provideZoneChangeDetection
} from '@angular/core';
import { provideRouter, Router, withInMemoryScrolling, withRouterConfig } from '@angular/router';
import localeEl from '@angular/common/locales/el';

import { routes } from './app.routes';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideToastr } from "ngx-toastr";
import {
    LocalStorageService,
    provideNgxWebstorage,
    withLocalStorage,
    withNgxWebstorageConfig,
    withSessionStorage
} from "ngx-webstorage";
import {
    HTTP_INTERCEPTORS,
    provideHttpClient,
    withFetch,
    withInterceptorsFromDi
} from "@angular/common/http";
import { HttpRequestsInterceptor } from "./core/interceptors/http-requests.interceptor";
import { registerLocaleData } from "@angular/common";
import { provideMomentDateAdapter } from "@angular/material-moment-adapter";
import { MAT_DATE_LOCALE } from "@angular/material/core";
import * as Sentry from '@sentry/angular';
import { provideLottieOptions } from 'ngx-lottie';
import player from 'lottie-web';
import { TG_INSTANCE, metaglottaInstance } from '@worthy-ventures/metaglotta-ngx';
import { environment } from '../environments/environment.prod';
registerLocaleData(localeEl, 'el');
export function LocaleFactory(ls: LocalStorageService): string {
    return ls.retrieve('preferred_lang') || 'en';
}

function trimSlashes(path: string) {
    if (path.endsWith('/')) {
        return path.slice(0, -1);
    }
    return path;
}

/**
 * Where the published JSON is fetched from.
 *
 * In production the bundle and the translations share an origin - www.fyes.eu
 * serves both the app and /meta/ - so the absolute url is a same-origin fetch
 * and CORS never applies. The dev server is a different origin, and the
 * CloudFront edge in front of the bucket caches the S3 response both with and
 * without `Access-Control-Allow-Origin`, depending on whether the request that
 * populated it carried an Origin header; a cross-origin fetch that lands on the
 * wrong cached variant fails with a 200 the browser refuses to hand over.
 * Routing through the dev-server proxy (`/meta` in proxy.config.json) makes it
 * same-origin from the browser's point of view and takes CORS out of the
 * picture, so local runs match production instead of depending on which edge
 * answered.
 *
 * Only localhost is rewritten - a dev build deployed to a real host keeps the
 * absolute url. Browsing `ng serve --host 0.0.0.0` over a LAN address is not
 * covered; add the address here if that is how you work.
 */
function metaglottaPrefix(): string {
    const servedLocally = typeof location !== 'undefined'
        && ['localhost', '127.0.0.1', '[::1]'].includes(location.hostname);
    return servedLocally ? '/meta/fyesgr' : environment.metaglottaCDN;
}

const i18nGetPath:any = (options:{namespace: any, language: any, prefix: any }) => {
    const {namespace, language, prefix } = options;
    const timestamp = Date.now();
    if (namespace) {
        return `${trimSlashes(prefix)}/${namespace}/${language}.json?t=${timestamp}`;
    } else {
        return `${trimSlashes(prefix)}/${language}.json?t=${timestamp}`;
    }
};

export const appConfig: ApplicationConfig = {
    providers: [
        provideZoneChangeDetection({ eventCoalescing: true }),
        provideRouter(routes,
             withInMemoryScrolling({
            scrollPositionRestoration: 'enabled',
            anchorScrolling: 'disabled',
        })
    //           withRouterConfig({
    //     onSameUrlNavigation: 'reload'
    //   })
    ),
        { provide: LOCALE_ID, useFactory: LocaleFactory, deps: [LocalStorageService] },
        { provide: MAT_DATE_LOCALE, useFactory: LocaleFactory, deps: [LocalStorageService] },
        // provideClientHydration(), // TODO enable this if we re enable SSR
        provideAnimationsAsync(),
        provideMomentDateAdapter(),
        provideToastr(),
        provideNgxWebstorage(
            withNgxWebstorageConfig({ separator: '', caseSensitive: true, prefix: '' }),
            withLocalStorage(),
            withSessionStorage()
        ),
        provideHttpClient(withFetch(), withInterceptorsFromDi()),
        { provide: HTTP_INTERCEPTORS, useClass: HttpRequestsInterceptor, multi: true },
        {
            provide: ErrorHandler,
            useValue: Sentry.createErrorHandler(),
        },
        {
            provide: Sentry.TraceService,
            deps: [Router],
        },
        {
            provide: APP_INITIALIZER,
            useFactory: () => () => { },
            deps: [Sentry.TraceService],
            multi: true,
        },
        provideLottieOptions({
            player: () => player,
        }),
        {
            provide: TG_INSTANCE,
            useFactory: (ls: LocalStorageService) => {
                const i18n = metaglottaInstance({
                    // Restore the visitor's last choice so a reload keeps the language.
                    language: ls.retrieve('preferred_lang') || 'en',
                    fallbackLanguage: 'en',
                    // Published JSON from the Metaglotta CDN. The prefix is the
                    // FOLDER: i18nGetPath appends `/<language>.json`, and it must
                    // stay set — an empty prefix resolves against the app's own
                    // origin and 404s into a page of bare keys rather than failing
                    // loudly.
                    backend: {prefix: metaglottaPrefix(), getPath: i18nGetPath},
                    // ICU is the default format. Verified: all 546 strings across
                    // en/el render byte-identical to their stored form.
                    //
                    // In-context editing, replacing Tolgee's DevTools(). Armed at
                    // runtime per browser rather than by a build flag: the extension
                    // supplies the editor's own API key, so no key lives in this repo
                    // and every write is attributed to the person who made it. Hold
                    // Alt to arm the overlay. Passed as a function so the bundler
                    // splits it into its own chunk — a page nobody is editing never
                    // downloads it.
                    //
                    // apiUrl is where the API lives, not a credential — it is only
                    // read once somebody supplies a key, and is needed because the
                    // extension usually sends a key without a url.
                    apiUrl: environment.metaglottaApiUrl,
                    loadEditor: () => import('@worthy-ventures/metaglotta-incontext-ng'),
                });
                i18n.run(); // starts the engine
                return i18n;
            },
            deps: [LocalStorageService],
        },
    ]
};
